import React from 'react';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { selectDelegateMode } from '@/redux/modules/auth/selectors';
import { setLocale } from '@/redux/modules/intl';
import { updatePreferences } from '@/redux/modules/auth';

// Components
import ComparisonSettingsOptions from './ComparisonSettingsOptions';
import { SelectInput, SelectOption } from '@/views/components/inputs';
import { Typography } from '@capintel/component-lib/typography';
import { withErrorBoundary } from '@/views/components/errors';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Legacy component
import LegacySettingsPreferences from './LegacySettingsPreferences';

// Libraries
import PerfectScrollbar from 'react-perfect-scrollbar';
import { connect } from 'react-redux';

// Types
import Locale from '@/types/locale';
import User from '@/types/user';
import UserPreferences from '@/types/user-preferences';
import UserPreferencesComparisonSettings from '@/types/user-preferences-comparison-settings';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Utils
import notification from '@/utils/notification';
import ZendeskUtils from '@/utils/3rd-party/zendesk';
import { LOCALE_OPTIONS as localeOptions, FormattedMessage, formatMessage } from '@/utils/i18n';

type DispatchProps = {
  setLocale: (locale: Locale) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
};

type StateProps = {
  delegateMode: boolean;
  locale: Locale;
};

type LegacyProps = {
  comparisonSettings: UserPreferencesComparisonSettings;
  onComparisonSettingsChange: (option: { [key: string]: string }) => void;
  onLocaleChange: (locale: Locale) => void;
  user: User;
};

type Props = LegacyProps & StateProps & DispatchProps;

const SettingsPreferencesV2: React.FC<Props> = ({
  user,
  locale,
  delegateMode,
  comparisonSettings,
  onComparisonSettingsChange,
  setLocale,
  updatePreferences,
}) => {
  const languageOptions = localeOptions.filter(option => user.appConfig.appLocales.includes(option.value));

  const onLanguageChange = async (language: Locale) => {
    if (language === locale) return;

    setLocale(language);
    await updatePreferences({ locale: language });
    ZendeskUtils.setLocale(language);
    // Use setTimeout to make sure language has been changed before sending the notification.
    setTimeout(() => notification.success(formatMessage('Settings.Preferences.LanguageChanged')), 0);
  };

  return (
    <div className="settings-preferences-v2 settings-tab" data-testid="settings-preferences">
      <PerfectScrollbar>
        <div className="settings-preferences">
          <div className="preferences">
            <div className="preference">
              <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
                <FormattedMessage id="Settings.Preferences.General.Title" />
              </Typography>
            </div>
            <div className="options">
              <SelectInput
                className="select-input-v2"
                label={formatMessage('Settings.Preferences.General.Fields.Language.Label')}
                onValueChange={onLanguageChange}
                selected={locale}
              >
                {languageOptions.map(option => (
                  <SelectOption key={option.value} value={option.value} lang={option.value}>
                    {option.label}
                  </SelectOption>
                ))}
              </SelectInput>
            </div>
          </div>

          {!delegateMode && (
            <ComparisonSettingsOptions user={user} comparisonSettings={comparisonSettings} onComparisonSettingsChange={onComparisonSettingsChange} />
          )}
        </div>
      </PerfectScrollbar>
    </div>
  );
};

const SettingsPreferences: React.FC<React.ComponentProps<typeof SettingsPreferencesV2>> = props => {
  const { isLightVersion } = useThemeVersion();

  if (isLightVersion) {
    return <SettingsPreferencesV2 {...props} />;
  }

  return <LegacySettingsPreferences {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  locale: state.intl.locale,
  delegateMode: selectDelegateMode(state),
});

export default connect(mapStateToProps, {
  setLocale,
  updatePreferences,
})(withErrorBoundary(SettingsPreferences));
